import { distanceInWordsToNow } from 'date-fns';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import { AnimatePresence, motion } from 'framer-motion'; // Import AnimatePresence and motion

import { useAuthContext } from 'AuthContext';
import { useMutation, useApolloClient } from '@apollo/client';
import CancelButton from 'components/buttons/Cancel';
import CommentsButton from 'components/buttons/Comments';
import DeleteButton from 'components/buttons/Delete';
import EditButton from 'components/buttons/Edit';
import LikeButton from 'components/buttons/Like';
import CommentButton from 'components/buttons/Comment';
// import ReportButton from 'components/buttons/Report';
import SaveButton from 'components/buttons/Save';
import CardActions from 'components/Card/Actions';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import EnhancedTextarea from 'components/EnhancedTextarea';
import HTMLFragment from 'components/HTMLFragment';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import CommentForm from 'components/CommentForm';
import Headshot from 'containers/Headshot';
import tracker from // TrackerFragments
  'lib/tracker';
import { convertToLocalDate } from '../../../utils/helper';
import { FEED_LIKE, DELETE_COMMENT, UPDATE_COMMENT, CREATE_COMMENT } from '../../../schema/apolloMutations/FeedMutations';
// import SvgIcon from '@mui/material/SvgIcon';
import CommentDeleteDialog from './DeleteDialog';
// import CommentFlagDialog from './FlagDialog';
import CommentReplies from './Replies';
import { GET_PROFILE_INFO } from '../../../schema/apolloQueries/commonQueries';

const validationSchema = Yup.object().shape({
  body: Yup.string()
    .trim()
    .required('Comment is required'),
});

const CommentInner = ({ comment, onDelete, onLikeOrUnlike, onEditComment, onReplyCount, viewer }) => {
  const { interceptAction } = useAuthContext();
  const formRef = useRef(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [liking, setLiking] = useState(false);
  const [replies, setReplies] = useState(false);
  const [replying, setReplying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const busy = useMemo(() => deleting || editing || flagging || replying, [
    deleting,
    editing,
    flagging,
    replying,
  ]);
  const [feedLike] = useMutation(FEED_LIKE);
  const [deletComment] = useMutation(DELETE_COMMENT);
  const [updateComment] = useMutation(UPDATE_COMMENT);
  const [createComment] = useMutation(CREATE_COMMENT);
  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });
  const personId = profileData?.getProfile?.personId
    ? profileData?.getProfile?.personId
    : null;
  // const priority = useMemo(() => {
  //   return (comment.preferences || []).indexOf('PRIORITY') > -1;
  // }, [comment.preferences]);
  const initialValues = useMemo(
    () => ({
      body: comment.commentInfo.body,
    }),
    [comment.commentInfo.body],
  );
  const isAuthor = useMemo(
    () => personId
      && personId === comment.personInfo.id,
    [comment, personId],
  );
  const trackerData = useMemo(
    () => ({
      comment,
      viewer,
    }),
    [comment, viewer],
  );
  // const hideFlagDialog = useCallback(() => setFlagging(false), [setFlagging]);
  const hideDeleteDialog = useCallback(() => setDeleting(false), [setDeleting]);
  const hideReplyForm = useCallback(() => {
    setReplying(false);
  }, [setReplying]);
  const onDeleteConfirm = useCallback(() => {
    deletComment({
      variables: {
        commentId: comment.commentInfo.id,
        eventId: comment.feedInfo.eventId,
      },
    }).then(res => {
      if (res) {
        onDelete();
        hideDeleteDialog();
      }
    });
  }, [hideDeleteDialog, onDelete]);
  const onUpdate = useCallback(
    (data,
      { setErrors, setSubmitting }) => {
      setUpdating(true);
      updateComment({
        variables: {
          commentId: comment.commentInfo.id,
          eventId: comment.feedInfo.eventId,
          comment: data.body,
        },
      }).then(res => {
        if (res) {
          tracker.trackGoogleAnalyticsEvent('Comments', 'Edit', trackerData);
          setEditing(false);
          setUpdating(false);
          setSubmitting(false);
        }
      }).catch(e => {
        tracker.trackGoogleAnalyticsException(e, trackerData);
        setUpdating(false);
        setSubmitting(false);
        setErrors(e);
      });
      onEditComment(data.body);
    },
    [comment, setEditing, setUpdating, trackerData],
  );

  const onLike = useCallback(() => {
    setLiking(true);
    feedLike({
      variables: {
        feedType: comment.feedType,
        id: comment.commentInfo.id,
        personId,
      },
    }).then(res => {
      if (res) {
        tracker.trackGoogleAnalyticsEvent(
          'Comments',
          comment.liked
            ? 'Unlike'
            : 'Like',
          trackerData,
        );
        onLikeOrUnlike(comment);
        setLiking(false);
      }
    }).catch(e => {
      tracker.trackGoogleAnalyticsException(e, trackerData);
    });
  }, [comment.id, interceptAction, viewer?.loggedIn]);
  const onReply = useCallback(
    (
      data,
      {
        resetForm = () => { },
        setErrors = () => { },
        setSubmitting = () => { },
      } = {},
    ) => {
      setSaving(true);
      createComment({
        variables: {
          comment: {
            body: data.body,
            foreignId: comment.feedInfo.eventId === 0 ? comment.sponsor.id : comment.feedInfo.eventId,
            model: comment.feedInfo.eventId === 0 ? 'Sponsor' : 'Event',
            parentId: comment.commentInfo.id,
            userId: personId,
          },
        },
      }).then(res => {
        if (res) {
          resetForm();
          setReplying(false);
          setSaving(false);
          setSubmitting(false);
          tracker.trackGoogleAnalyticsEvent(
            'Comments',
            'Create reply',
            trackerData,
          );
          onReplyCount();
        }
      }).catch(e => {
        setErrors(e);
        setSaving(false);
        setSubmitting(false);
        tracker.trackGoogleAnalyticsException(e, trackerData);
      });
      setSaving(true);
    },
    [setReplying, setSaving, trackerData],
  );
  const renderUpdateForm = useCallback(
    ({ handleBlur, handleChange, isSubmitting, values }) => (
      <EnhancedTextarea
        autoFocus
        disabled={isSubmitting}
        name='body'
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.body}
      />
    ),
    [],
  );
  const showFlagDialog = useCallback(() => setFlagging(true), [setFlagging]);
  const showDeleteDialog = useCallback(() => setDeleting(true), [setDeleting]);
  const showReplyForm = useCallback(() => {
    setReplying(true);
  }, [setReplying]);
  const startEditing = useCallback(() => {
    setEditing(true);
  }, [setEditing]);
  const stopEditing = useCallback(() => {
    setEditing(false);
  }, [setEditing]);
  const submitForm = useCallback(() => formRef.current.submitForm(), [formRef]);
  const toggleReplies = useCallback(() => {
    if (comment.feedInfo.commentCount === 0) {
      showReplyForm();
    } else {
      setReplies(!replies);
    }
  }, [comment.feedInfo.commentCount, showReplyForm, replies]);

  return (
    <LoadBlur loading={updating}>
      <CardHeader
        avatar={<Headshot person={comment.personInfo} />}
        title={comment.personInfo.displayName}
        subheader={`${distanceInWordsToNow(
          convertToLocalDate(comment.feedInfo.created),
        )} ago`}
      >
        {/* {priority && (
          <span title='Pinned' style={{ float: 'right' }}>
            <SvgIcon>
              <path
                fill=''
                d='M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45-1 1-1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z'
              />
            </SvgIcon>
          </span>
        )} */}
      </CardHeader>
      <CardText noTopPadding>
        {useMemo(
          () => (editing ? (
            <Formik
              isInitialValid
              initialValues={initialValues}
              onSubmit={onUpdate}
              ref={formRef}
              validationSchema={validationSchema}
            >
              {renderUpdateForm}
            </Formik>
          ) : (
            <HTMLFragment>{comment.commentInfo.body}</HTMLFragment>
          )),
          [
            comment.commentInfo.body,
            editing,
            initialValues,
            onUpdate,
            renderUpdateForm,
          ],
        )}
      </CardText>
      <CardActions noTopPadding>
        {useMemo(() => {
          if (editing) {
            return (
              <Fragment>
                <RowSpacer />
                <CancelButton onClick={stopEditing} secondary />
                <SaveButton onClick={submitForm} primary />
              </Fragment>
            );
          }
          return (
            <Fragment>
              <LikeButton
                disabled={liking}
                liked={comment.feedInfo.liked}
                likes={comment.feedInfo.likeCount}
                onClick={onLike}
                primary={!liking}
              />
              <CommentsButton
                comments={comment.feedInfo.commentCount}
                disabled={busy}
                onClick={toggleReplies}
                primary={!busy}
                title='Replies'
              />
              <CommentButton
                disabled={busy}
                onClick={showReplyForm}
                primary={!busy}
              />
              <RowSpacer />
              {/* {isAuthor ? ( */}
              <Fragment>
                <EditButton
                  disabled={busy}
                  onClick={startEditing}
                  primary={!busy}
                />
                <DeleteButton
                  disabled={busy}
                  onClick={showDeleteDialog}
                  primary={!busy}
                />
              </Fragment>
              {/* // ) : (
              //   <ReportButton
              //     disabled={busy}
              //     flagged={comment.flagged}
              //     onClick={showFlagDialog}
              //     primary={!busy}
              //   />
              // )} */}
            </Fragment>
          );
        }, [
          busy,
          // comment.flagged,
          comment.feedInfo.commentCount,
          comment.feedInfo.likeCount,
          comment.feedInfo.liked,
          // comment.replyCount,
          editing,
          isAuthor,
          liking,
          onLike,
          showDeleteDialog,
          showFlagDialog,
          showReplyForm,
          startEditing,
          stopEditing,
          submitForm,
          toggleReplies,
        ])}
      </CardActions>
      <AnimatePresence>
        {replying && (
          <motion.div
            key='reply-form'
            initial={{ opacity: 0, y: 20 }} // Animation when entering
            animate={{ opacity: 1, y: 0 }} // Animation when visible
            exit={{ opacity: 0, y: 20 }} // Animation when exiting
            transition={{ duration: 0.3 }} // Duration of the animation
          >
            <CommentForm
              autoFocus
              isReply
              onCancel={hideReplyForm}
              onSave={onReply}
              saving={saving}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <CommentDeleteDialog
        onConfirm={onDeleteConfirm}
        onRequestClose={hideDeleteDialog}
        open={deleting}
      />
      {useMemo(
        () => replies
          && comment.feedInfo.commentCount > 0 && <CommentReplies comment={comment} />,
        [comment.feedInfo.commentCount, replies],
      )}
      {/* {useMemo(
        () => (isAuthor ? (
          <CommentDeleteDialog
              onConfirm={onDeleteConfirm}
              onRequestClose={hideDeleteDialog}
              open={deleting}
          />
          ) : (
            <CommentFlagDialog
              comment={comment}
              onRequestClose={hideFlagDialog}
              open={flagging}
              viewer={viewer}
            />
          )),
        [
          comment,
          deleting,
          flagging,
          hideDeleteDialog,
          hideFlagDialog,
          onDeleteConfirm,
          isAuthor,
          viewer,
        ],
      )} */}
    </LoadBlur>
  );
};

CommentInner.propTypes = {
  comment: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  onLikeOrUnlike: PropTypes.func.isRequired,
  onEditComment: PropTypes.func.isRequired,
  onReplyCount: PropTypes.func.isRequired,
  viewer: PropTypes.object.isRequired,
  resizeAllGridItems: PropTypes.func,
};

export default CommentInner;
